import React, { useEffect, useState } from "react";
import { Edit, Trash, Plus, X, ImageIcon, VideoIcon } from 'lucide-react';
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import api from '../../../Axios'


export default function ManageEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newImages, setNewImages] = useState(null);
  const [newVideos, setNewVideos] = useState(null);
  const [images, setImages] = useState(null);
  const [videos, setVideos] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalMedia, setModalMedia] = useState(null);
  const [updatedEventData, setUpdatedEventData] = useState({
    title: "",
    description: "",
    date: "",
    location: "",
    organizer: "",
    images: [],
    videos: [],
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_BASE_URL}/api/v1/events/all`);
        setEvents(response.data);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Failed to fetch events. Please try again.");
      } finally {
        setLoading(false);
      }
    };
  
    fetchEvents();
  }, []);
  const handleUpdate = (id) => {
    const eventToUpdate = events.find((event) => event._id === id);
    if (eventToUpdate) {
      setSelectedEvent(eventToUpdate);
      setUpdatedEventData(eventToUpdate);
    } else {
      toast.error("Event not found.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedEventData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, type) => {
    const files = e.target.files;
    if (type === "images") {
      setNewImages(files);
    } else if (type === "videos") {
      setNewVideos(files);
    }
  };

  // Submit the updated event using fetch API
  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
  
    if (!selectedEvent) {
      toast.error("No event selected for update.");
      return;
    }
  
    try {
      const formData = new FormData();
  
      // Add updatedEventData to formData
      Object.entries(updatedEventData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(`${key}[]`, item));
        } else {
          formData.append(key, value);
        }
      });
  
      // Add newImages and newVideos to formData
      if (newImages) {
        Array.from(newImages).forEach((image) => formData.append("images", image));
      }
      if (newVideos) {
        Array.from(newVideos).forEach((video) => formData.append("videos", video));
      }
  
      // Use the api instance for the PUT request
      const response = await api.put(`/api/v1/events/update/${selectedEvent._id}`, formData);
  
      if (response.status === 200) {
        setEvents((prev) =>
          prev.map((event) => (event._id === selectedEvent._id ? response.data : event))
        );
        toast.success("Event updated successfully!");
        setSelectedEvent(null);
      } else {
        throw new Error(response.data.message || "Failed to update event.");
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to update event.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/v1/events/delete/${id}`);
      setEvents((prev) => prev.filter((event) => event._id !== id));
      toast.success("Event deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete event.");
    }
  };

  const openMediaModal = (media) => {
    setModalMedia(media);
  };

  const closeMediaModal = () => {
    setModalMedia(null);
  };

  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (error) return <div className="flex items-center justify-center h-screen text-red-500">Error: {error}</div>;

  return (
    <div className="container px-4 py-8 mx-auto">
      <ToastContainer />
      <h1 className="mb-6 text-3xl font-bold text-gray-800">Manage Events</h1>
      <div className="mb-4 text-right">
        <button
          onClick={() => navigate('/admin/addeventform')}
          className="flex items-center justify-center px-4 py-2 text-white transition duration-300 ease-in-out bg-green-500 rounded-md hover:bg-green-600"
        >
          <Plus size={20} className="mr-2" />
          Add New Event
        </button>
      </div>
      {selectedEvent && (
        <form onSubmit={handleSubmitUpdate} className="p-6 mb-8 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-gray-700">Update Event</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <input
              type="text"
              name="title"
              value={updatedEventData.title}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Event Title"
            />
            <input
              type="date"
              name="date"
              value={updatedEventData.date}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              name="location"
              value={updatedEventData.location}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Event Location"
            />
            <input
              type="text"
              name="organizer"
              value={updatedEventData.organizer}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Event Organizer"
            />
            <textarea
              name="description"
              value={updatedEventData.description}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 md:col-span-2"
              placeholder="Event Description"
              rows={4}
            />
          </div>
          <div className="mt-4">
            <h3 className="mb-2 text-lg font-semibold">Add/Update Media</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium">Images</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileChange(e, "images")}
                  className="p-2 border rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium">Videos</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileChange(e, "videos")}
                  className="p-2 border rounded-md"
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <h3 className="mb-2 text-lg font-semibold">Current Media</h3>
            <div className="flex flex-wrap gap-2">
              {selectedEvent && selectedEvent.images && selectedEvent.images.map((image, index) => (
                <div key={index} className="relative w-24 h-24">
                  <img
                    src={`/api/uploads/events/${image}`}
                    alt="Event"
                    className="object-cover w-full h-full rounded"
                  />
                  <div className="absolute inset-0 flex items-center justify-center text-white bg-black bg-opacity-50 rounded">
                    <ImageIcon size={24} />
                  </div>
                </div>
              ))}
              {updatedEventData.videos.map((url, index) => (
                <div key={index} className="relative w-24 h-24">
                  <video
                    src={url}
                    className="object-cover w-full h-full rounded"
                    controls
                  />
                  <div className="absolute inset-0 flex items-center justify-center text-white bg-black bg-opacity-50 rounded">
                    <VideoIcon size={24} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button
            type="submit"
            className="w-full py-3 mt-4 text-white bg-blue-500 rounded-md hover:bg-blue-600"
          >
            Save Changes
          </button>
        </form>
      )}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {events.map((event) => (
          <div key={event._id} className="overflow-hidden bg-white rounded-lg shadow-md">
            <div className="p-4">
              <h2 className="mb-2 text-xl font-semibold text-gray-800">{event.title}</h2>
              <p className="mb-2 text-gray-600 line-clamp-2">{event.description}</p>
              <p className="mb-1 text-sm text-gray-500">Date: {new Date(event.date).toLocaleDateString()}</p>
              <p className="mb-1 text-sm text-gray-500">Location: {event.location}</p>
              <p className="mb-4 text-sm text-gray-500">Organizer: {event.organizer}</p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {event.images.length > 0 ? (
                  event.images.map((image, index) => (
                    <div 
                      key={index}
                      className="relative w-20 h-20 cursor-pointer"
                      onClick={() => openMediaModal(`/api/uploads/events/${image}`)}
                    >
                      <img
                        src={`/api/uploads/events/${image}`}
                        alt={event.title}
                        className="object-cover w-full h-full rounded"
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-white bg-black bg-opacity-50 rounded">
                        <ImageIcon size={24} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-center w-20 h-20 text-gray-400 bg-gray-200 rounded">
                    <ImageIcon size={24} />
                  </div>
                )}
                {event.videos.map((url, index) => (
                  <div 
                    key={index}
                    className="relative w-20 h-20 cursor-pointer"
                    onClick={() => openMediaModal(`/api/uploads/events/${url}`)}
                  >
                    <video
                      src={`/api/uploads/events/${url}`}
                      className="object-cover w-full h-full rounded"
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-white bg-black bg-opacity-50 rounded">
                      <VideoIcon size={24} />
                    </div>
                  </div>
                ))}
                {event.images.length === 0 && event.videos.length === 0 && (
                  <div className="flex items-center justify-center w-20 h-20 text-gray-400 bg-gray-200 rounded">
                    No Media
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => handleUpdate(event._id)}
                  className="p-2 text-blue-500 transition duration-300 ease-in-out hover:text-blue-700"
                >
                  <Edit size={20} />
                </button>
                <button
                  onClick={() => handleDelete(event._id)}
                  className="p-2 text-red-500 transition duration-300 ease-in-out hover:text-red-700"
                >
                  <Trash size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modalMedia && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={closeMediaModal}
        >
          <div 
            className="relative w-full max-w-4xl mx-4 overflow-hidden bg-white rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeMediaModal}
              className="absolute p-2 text-white transition duration-300 ease-in-out bg-red-500 rounded-full top-2 right-2 hover:bg-red-600"
            >
              <X size={24} />
            </button>
            {modalMedia.includes('.mp4') ? (
              <video src={modalMedia} controls className="w-full h-auto max-h-[80vh]" />
            ) : (
              <img src={modalMedia} alt="Event media" className="w-full h-auto max-h-[80vh] object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

